import { IEventV2 } from '../../../data/entities/event/index';
import { getGroupFromSource } from './helpers';

export interface IEmailTemplateInfo {
	name: string;
	target: 'owner' | 'guest';
	options?: Record<string, (event: IEventV2) => Record<string, string> | undefined>;
}

const cancellationPolicyMapper = (event: IEventV2) => {
	const group = getGroupFromSource(event.source);

	if (group === 'airbnb') {
		return {
			airbnb_superstrict_60: 'AirBnB Superstrict 60',
			airbnb_superstrict_30: 'AirBnB Superstrict 30',
			airbnb_strict_14_with_grace_period: 'AirBnB Strict 14 With Grace Period',
			airbnb_better_strict_with_grace_period: 'AirBnB Firm',
			airbnb_moderate: 'AirBnB Moderate',
		};
	}

	if (group === 'booking.com') {
		return {
			bookingcom_fully_non_refundable: 'Booking.com Fully Non-Refundable',
			bookingcom_60: 'Booking.com Refundable up to 60 days before arrival',
			bookingcom_30: 'Booking.com Refundable up to 30 days before arrival',
			bookingcom_14: 'Booking.com Refundable up to 14 days before arrival',
			bookingcom_7: 'Booking.com Refundable up to 7 days before arrival',
			bookingcom_5: 'Booking.com Refundable up to 5 days before arrival',
		};
	}

	if (group === 'expedia') {
		return {
			expedia_fully_non_refundable: 'Expedia Fully Non-Refundable',
			expedia_fully_refundable_60: 'Expedia Fully Refundable 60',
			expedia_fully_refundable_28: 'Expedia Fully Refundable 28',
			expedia_fully_refundable_14: 'Expedia Fully Refundable 14',
			expedia_fully_refundable_7: 'Expedia Fully Refundable 7',
			expedia_fully_refundable_5: 'Expedia Fully Refundable 5',
		};
	}

	if (group === 'holidu') {
		return {
			holidu_super_strict_v1: 'Holidu Fully Non-Refundable',
			holidu_strict_v1: 'Holidu Refundable up to 30 days before arrival',
			holidu_moderate_v1: 'Holidu Refundable up to 14 days before arrival',
			holidu_flexible_v1: 'Holidu Refundable up to 5 days before arrival',
		};
	}

	if (group === 'homeaway') {
		return {
			homeaway_no_refund: 'HomeAway No Refund',
			homeaway_strict: 'HomeAway Strict',
			homeaway_firm: 'HomeAway Firm',
			homeaway_moderate: 'HomeAway Moderate',
			homeaway_relaxed: 'HomeAway Relaxed'
		};
	}

	if (group === 'tripadvisor') {
		return {
			tripadvisor_superstrict: 'Tripadvisor Superstrict',
			tripadvisor_strict: 'Tripadvisor Strict',
			tripadvisor_moderate: 'Tripadvisor Moderate',
			tripadvisor_flexible: 'Tripadvisor Flexible',
			tripadvisor_relaxed: 'Tripadvisor Relaxed'
		};
	}

	if (group === 'tui') {
		return {
			tui_fully_refundable_30: 'TUI Refundable up to 30 days before arrival'
		};
	}

	if (group === 'direct') {
		return {
			travelnest_direct_super_strict_v1: 'Travelnest Direct Fully Non-Refundable',
			travelnest_direct_strict_v1: 'Travelnest Direct Refundable up to 30 days before arrival',
			travelnest_direct_moderate_v1: 'Travelnest Direct Refundable up to 14 days before arrival',
			travelnest_direct_flexible_v1: 'Travelnest Direct Refundable up to 5 days before arrival',
		};
	}

	if (group === 'plumguide') {
		return {
			plumguide_superfirm_60: 'Plum Guide Super Firm 60',
			plumguide_superfirm_30: 'Plum Guide Super Firm 30',
			plumguide_moderate: 'Plum Guide Moderate',
			plumguide_reasonable: 'Plum Guide Reasonable',
			plumguide_relaxed: 'Plum Guide Relaxed',
		};
	}

	if (group === 'hometogo') {
		return {
			hometogo_moderate_v1: 'HomeToGo Refundable up to 30 days before arrival',
			hometogo_custom_moderate_v1: 'HomeToGo Refundable up to 14 days before arrival',
			hometogo_custom_flexible_v1: 'HomeToGo Refundable up to 5 days before arrival',
		};
	}

	return;
};

export const emailTemplates = new Map([
	[
		'owner_booking_updated',
		{
			name: 'Booking Updated',
			target: 'owner'
		} as IEmailTemplateInfo
	],
	[
		'owner_booking_confirmation',
		{
			name: 'Booking Confirmation',
			target: 'owner',
			options: {
				cancellation_policy: cancellationPolicyMapper
			}
		} as IEmailTemplateInfo
	],
	[
		'owner_booking_cancelled',
		{
			name: 'Booking Cancelled (Owner cancellation)',
			target: 'owner'
		} as IEmailTemplateInfo
	],
	[
		'owner_guest_booking_cancelled',
		{
			name: 'Booking Cancelled (Guest cancellation)',
			target: 'owner',
			options: {
				cancellation_policy: cancellationPolicyMapper,
				cancellation_reason: (e) => ({
					standard: 'Standard',
					coronavirus: 'Coronavirus'
				})
			}
		} as IEmailTemplateInfo
	],
	[
		'guest_booking_confirmation',
		{
			name: 'Booking Confirmation',
			target: 'guest'
		} as IEmailTemplateInfo
	],
	[
		'guest_security_deposit_held',
		{
			name: 'Security Deposit Held',
			target: 'guest'
		} as IEmailTemplateInfo
	],
	[
		'guest_security_deposit_request',
		{
			name: 'Security Deposit Request',
			target: 'guest'
		} as IEmailTemplateInfo
	],
	[
		'guest_payment_declined',
		{
			name: 'Payment Declined',
			target: 'guest'
		} as IEmailTemplateInfo
	],
	[
		'guest_review_reminder',
		{
			name: 'Request Review',
			target: 'guest'
		} as IEmailTemplateInfo
	]
]);

export const defaultEmailTemplateId = 'owner_booking_updated';
